import React from "react";
import { Badge, Empty } from "antd";
import moment from "moment";
import {
  AiOutlineCheckCircle as AiOutlineAttendance,
  AiOutlineDollarCircle,
  AiOutlineExclamationCircle,
  AiOutlineFileText,
  AiOutlineNotification,
  AiOutlineWarning,
} from "react-icons/ai";
import {
  FaChalkboardTeacher,
  FaCertificate,
  FaUserClock,
  FaUserGraduate,
} from "react-icons/fa";
import { HiOutlineAcademicCap } from "react-icons/hi";
import {
  Card,
  CardTitle,
  LinkRow,
  SectionShell,
  TrendKpiCard,
} from "../blocks";
import { SECTION_COLORS, formatCompactCurrency } from "../chartTheme";
const TILE_COLORS = {
  students: { accentColor: "#6366f1", iconBg: "#eef2ff", iconColor: "#6366f1" },
  attendance: { accentColor: "#3b82f6", iconBg: "#eff6ff", iconColor: "#3b82f6" },
  fee: { accentColor: "#10b981", iconBg: "#ecfdf5", iconColor: "#10b981" },
  staff: { accentColor: "#8b5cf6", iconBg: "#f5f3ff", iconColor: "#8b5cf6" },
  pendingFees: { accentColor: "#f59e0b", iconBg: "#fffbeb", iconColor: "#f59e0b" },
  classes: { accentColor: "#f43f5e", iconBg: "#fff1f2", iconColor: "#f43f5e" },
};

const stripHtml = (html) =>
  String(html || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const OverviewSection = ({
  overview,
  attendance,
  fee,
  staffOps,
  activity,
  distribution,
  onNavigate,
  onJumpToSection,
}) => {
  const attendanceTrendPct = (() => {
    const trend = attendance.trend || [];
    const todayKey = moment().format("YYYY-MM-DD");
    const priorDay = [...trend]
      .filter((d) => d.date !== todayKey && d.markedCount > 0)
      .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
    const today = trend.find((d) => d.date === todayKey);
    if (!priorDay || !today) return null;
    return Math.round((today.percentage - priorDay.percentage) * 10) / 10;
  })();

  const byGender = distribution?.byGender || [];
  const boysCount = byGender.find((g) => g.gender === "male")?.count || 0;
  const girlsCount = byGender.find((g) => g.gender === "female")?.count || 0;

  const collectedPctOfExpected =
    fee.expected > 0 ? Math.round((fee.collected / fee.expected) * 100) : 0;

  const actionItems = [
    {
      label: "Payment approvals",
      caption: "Transactions waiting for review",
      count: overview.pendingTransactions,
      route: "/dashboard/admin/menu/fee/approve",
      icon: <AiOutlineDollarCircle size={18} />,
      iconBg: "#ecfdf5",
      iconColor: "#10b981",
    },
    {
      label: "Discount approvals",
      caption: "Discount requests pending",
      count: overview.pendingDiscounts,
      route: "/dashboard/admin/menu/fee/approve-discount",
      icon: <AiOutlineExclamationCircle size={18} />,
      iconBg: "#fffbeb",
      iconColor: "#f59e0b",
    },
    {
      label: "Admission applications",
      caption: "New applications to process",
      count: overview.pendingApplications,
      route: "/dashboard/admin/menu/admission",
      icon: <AiOutlineFileText size={18} />,
      iconBg: "#eff6ff",
      iconColor: "#3b82f6",
    },
    {
      label: "Staff leave requests",
      caption: "Permission / leave awaiting review",
      count: staffOps.leaveRequests?.pending || 0,
      route: "/dashboard/admin/menu/instructor-attendance",
      icon: <FaUserClock size={16} />,
      iconBg: "#fff1f2",
      iconColor: "#f43f5e",
    },
    {
      label: "Certificate builder requests",
      caption: "Parent certificate requests awaiting review",
      count: overview.pendingCertificateRequests || 0,
      route: "/dashboard/admin/menu/certificate-builder",
      icon: <FaCertificate size={16} />,
      iconBg: "#f5f3ff",
      iconColor: "#8b5cf6",
    },
  ];

  const activityFeed = (activity.announcements || [])
    .map((a) => ({
      key: `a-${a._id}`,
      title: "Announcement",
      detail: stripHtml(a.content).slice(0, 90) || "New school post",
      date: a.createdAt,
    }))
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 8);

  return (
    <SectionShell
      id="overview"
      title="Overview"
      subtitle="Your school at a glance — tap a tile to jump to its section"
      accentColor={SECTION_COLORS.overview}
    >
      <div className="kpi-grid-3col">
        <TrendKpiCard
          icon={<FaUserGraduate size={24} />}
          {...TILE_COLORS.students}
          value={(overview.totalStudents || 0).toLocaleString("en-IN")}
          label="Total Students"
          caption={`${boysCount.toLocaleString("en-IN")} Boys · ${girlsCount.toLocaleString(
            "en-IN"
          )} Girls across ${overview.totalClasses || 0} classes`}
          trend={overview.trends?.totalStudents}
          onClick={() => onJumpToSection("students")}
        />
        <TrendKpiCard
          icon={<AiOutlineAttendance size={24} />}
          {...TILE_COLORS.attendance}
          value={`${attendance.today?.percentage || 0}%`}
          label="Attendance Rate"
          caption={`${attendance.today?.present || 0} present · ${
            attendance.today?.absent || 0
          } absent · ${attendance.today?.notMarked || 0} not marked today`}
          trend={attendanceTrendPct}
          onClick={() => onJumpToSection("students")}
        />
        <TrendKpiCard
          icon={<AiOutlineDollarCircle size={24} />}
          {...TILE_COLORS.fee}
          value={formatCompactCurrency(fee.collected)}
          label="Fee Collected"
          caption={`Target ${formatCompactCurrency(fee.expected)} · ${collectedPctOfExpected}% achieved`}
          trend={fee.trends?.collected}
          onClick={() => onJumpToSection("finance")}
        />
        <TrendKpiCard
          icon={<FaChalkboardTeacher size={24} />}
          {...TILE_COLORS.staff}
          value={(overview.totalTeachers || 0).toLocaleString("en-IN")}
          label="Staff Members"
          caption={`${overview.totalSubjects || 0} subjects taught`}
          trend={overview.trends?.totalTeachers}
          onClick={() => onJumpToSection("staff")}
        />
        <TrendKpiCard
          icon={<AiOutlineWarning size={24} />}
          {...TILE_COLORS.pendingFees}
          value={formatCompactCurrency(fee.remaining)}
          label="Pending Fees"
          caption={`${(fee.studentsWithDues || 0).toLocaleString(
            "en-IN"
          )} students with outstanding dues`}
          trend={fee.trends?.remaining}
          onClick={() => onJumpToSection("finance")}
        />
        <TrendKpiCard
          icon={<HiOutlineAcademicCap size={24} />}
          {...TILE_COLORS.classes}
          value={(overview.totalClasses || 0).toLocaleString("en-IN")}
          label="Classes"
          caption={`${overview.totalSubjects || 0} subjects across all classes`}
          trend={overview.trends?.totalClasses}
          onClick={() => onJumpToSection("students")}
        />
      </div>

      <div className="k360-grid k360-cols-2">
        <Card accentColor={SECTION_COLORS.overview}>
          <CardTitle
            title="Action Center"
            subtitle="Items waiting for your decision"
          />
          <div className="space-y-3">
            {actionItems.map((item) => (
              <LinkRow
                key={item.label}
                icon={item.icon}
                iconBg={item.iconBg}
                iconColor={item.iconColor}
                label={item.label}
                caption={item.caption}
                right={
                  <Badge
                    count={item.count}
                    showZero
                    style={{
                      backgroundColor: item.count > 0 ? "#f43f5e" : "#cbd5e1",
                      fontWeight: 700,
                    }}
                  />
                }
                onClick={() => onNavigate(item.route)}
              />
            ))}
          </div>
        </Card>

        <Card accentColor={SECTION_COLORS.overview}>
          <CardTitle
            title="Recent Activity"
            subtitle={`${overview.announcementsThisMonth} announcements this month`}
          />
          {activityFeed.length > 0 ? (
            <div className="space-y-2.5 max-h-80 overflow-y-auto pr-1">
              {activityFeed.map((item) => (
                <div
                  key={item.key}
                  className="flex items-start p-3 rounded-xl hover:bg-slate-50 transition-colors"
                  style={{ gap: "1.1rem" }}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                    style={{ backgroundColor: "#eff6ff", color: "#3b82f6" }}
                  >
                    <AiOutlineNotification size={17} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-slate-700 truncate">
                        {item.title}
                      </span>
                      <span className="text-xs text-slate-400 whitespace-nowrap">
                        {item.date ? moment(item.date).fromNow() : ""}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 truncate mt-0.5">
                      {item.detail}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Empty description="No recent activity" className="py-12" />
          )}
        </Card>
      </div>
    </SectionShell>
  );
};

export default OverviewSection;
